import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, CheckCircle, XCircle, Mail, MailX } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('idle'); // idle, loading, success, error, resubscribed
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  // Check if there's an email and token in the URL (from email link)
  useEffect(() => {
    const emailParam = searchParams.get('email');
    const tokenParam = searchParams.get('token');

    if (emailParam && tokenParam) {
      setEmail(emailParam);
      handleUnsubscribeWithToken(emailParam, tokenParam);
    }
  }, [searchParams]);

  const handleUnsubscribeWithToken = async (emailAddr, token) => {
    setStatus('loading');
    try {
      const response = await axios.get(`${API_URL}/unsubscribe`, {
        params: { email: emailAddr, token: token }
      });
      setStatus('success');
      setMessage(response.data.message);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Failed to unsubscribe. Please try again.');
    }
  };

  const handleManualUnsubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setStatus('loading');
    try {
      const response = await axios.post(`${API_URL}/unsubscribe`, { email });
      setStatus('success');
      setMessage(response.data.message);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Failed to unsubscribe. Please try again.');
    }
  };

  const handleResubscribe = async () => {
    if (!email) return;

    setStatus('loading');
    try {
      const response = await axios.post(`${API_URL}/resubscribe`, { email });
      setStatus('resubscribed');
      setMessage('You have been resubscribed to our emails!');
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Failed to resubscribe. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A2342] to-[#1a4a7a] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-[#0A2342] p-6 text-center">
            <h1 className="text-2xl font-bold text-white">Just Dive</h1>
            <p className="text-[#FF6B35] mt-1">Email Preferences</p>
          </div>

          {/* Content */}
          <div className="p-8">
            {status === 'loading' && (
              <div className="text-center py-8">
                <Loader2 className="w-12 h-12 animate-spin text-[#0A2342] mx-auto" />
                <p className="mt-4 text-gray-600">Processing your request...</p>
              </div>
            )}

            {status === 'success' && (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800 mb-2">Unsubscribed Successfully</h2>
                <p className="text-gray-600 mb-6">
                  You've been unsubscribed from our marketing emails. We're sorry to see you go!
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  Changed your mind?
                </p>
                <Button
                  onClick={handleResubscribe}
                  variant="outline"
                  className="border-[#FF6B35] text-[#FF6B35] hover:bg-[#FF6B35] hover:text-white"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Resubscribe
                </Button>
              </div>
            )}

            {status === 'resubscribed' && (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-10 h-10 text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800 mb-2">Welcome Back!</h2>
                <p className="text-gray-600 mb-6">
                  You've been resubscribed to our emails. We're excited to keep you updated on dive trips, deals, and more!
                </p>
              </div>
            )}

            {status === 'error' && (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800 mb-2">Something Went Wrong</h2>
                <p className="text-gray-600 mb-6">{message}</p>
                <Button
                  onClick={() => setStatus('idle')}
                  variant="outline"
                >
                  Try Again
                </Button>
              </div>
            )}

            {status === 'idle' && (
              <div>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MailX className="w-10 h-10 text-gray-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-800 mb-2">Unsubscribe from Emails</h2>
                  <p className="text-gray-600 text-sm">
                    Enter your email address below to unsubscribe from our marketing emails.
                  </p>
                </div>

                <form onSubmit={handleManualUnsubscribe} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full"
                      data-testid="unsubscribe-email-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[#0A2342] hover:bg-[#0A2342]/90"
                    data-testid="unsubscribe-submit-btn"
                  >
                    Unsubscribe
                  </Button>
                </form>

                <p className="text-center text-xs text-gray-500 mt-6">
                  You will still receive transactional emails (order confirmations, trip bookings, etc.)
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-8 py-4 text-center border-t">
            <Link to="/" className="text-[#FF6B35] hover:underline text-sm">
              ← Back to Just Dive
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnsubscribePage;
